/**
 * MealPlanCard — accessible card that renders a MealPlan produced by the engine.
 *
 * Security (CLAUDE.md §4):
 *   All LLM-sourced strings (agentRationale, foodName, meal labels) are rendered
 *   via React's safe text-node binding — NEVER via dangerouslySetInnerHTML.
 *
 * Accessibility (CLAUDE.md §7, spec §8.2):
 *   - Semantic HTML: article > h2 (card title), h3 (meals), ul/li (items)
 *   - Convergence status: text label + aria-live (not color alone)
 *   - Interactive controls ≥ 44 × 44 px
 *   - WCAG 2.1 AA contrast via Tailwind dark-palette classes
 *   - aria-live="polite" on convergence region
 *
 * States: loading / error / empty (no plan prop) / plan.
 */

import type { MealPlan, Meal, MealItem, ConvergenceStatus, MacroDelta } from "@autodieta/shared";
import { formatGrams, formatKcal, formatMacro, formatDelta } from "../utils/format.js";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MealPlanCardProps {
  /** The fully resolved meal plan from the engine. */
  mealPlan?: MealPlan;
  /** When true, shows a loading skeleton. Mutually exclusive with mealPlan/error. */
  loading?: boolean;
  /** When set, shows an error message. Mutually exclusive with mealPlan/loading. */
  error?: string;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function MealPlanCard({ mealPlan, loading, error }: MealPlanCardProps): JSX.Element {
  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  if (!mealPlan) {
    return <EmptyState />;
  }

  return <PlanContent plan={mealPlan} />;
}

// ---------------------------------------------------------------------------
// Loading state
// ---------------------------------------------------------------------------

function LoadingState(): JSX.Element {
  return (
    <div
      role="status"
      aria-label="Cargando plan nutricional"
      className="flex flex-col gap-3 rounded-xl border border-zinc-700 bg-zinc-900 p-4"
    >
      <div className="h-5 w-48 animate-pulse rounded bg-zinc-700" />
      <div className="h-4 w-full animate-pulse rounded bg-zinc-800" />
      <div className="h-4 w-3/4 animate-pulse rounded bg-zinc-800" />
      <span className="sr-only">Cargando plan nutricional…</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function ErrorState({ message }: { message: string }): JSX.Element {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="rounded-xl border border-red-800/60 bg-red-950/20 px-4 py-3 text-sm text-red-400"
    >
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState(): JSX.Element {
  return (
    <p className="py-4 text-center text-sm text-zinc-500">
      No hay plan nutricional disponible.
    </p>
  );
}

// ---------------------------------------------------------------------------
// Plan content
// ---------------------------------------------------------------------------

function PlanContent({ plan }: { plan: MealPlan }): JSX.Element {
  const date = formatDate(plan.createdAt);

  return (
    <article
      aria-label="Plan nutricional"
      className="flex flex-col gap-4 rounded-xl border border-zinc-700 bg-zinc-900 p-4"
    >
      {/* Card heading */}
      <header>
        <h2 className="text-base font-semibold text-zinc-100">
          Plan nutricional
        </h2>
        <p className="text-xs text-zinc-500">{date}</p>
      </header>

      {/* Convergence status — aria-live for screen reader announcements */}
      <ConvergenceIndicator status={plan.convergenceStatus} />

      {/* Target summary */}
      <TargetSummary targets={plan.targets} />

      {/* Meals */}
      {plan.meals.map((meal) => (
        <MealSection key={meal.label} meal={meal} />
      ))}

      {/* Day totals */}
      <TotalsSection totals={plan.totals} />

      {/* Agent rationale — rendered via textContent binding (XSS-safe) */}
      {plan.agentRationale ? (
        <RationaleSection rationale={plan.agentRationale} />
      ) : null}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Convergence indicator
// ---------------------------------------------------------------------------

function ConvergenceIndicator({ status }: { status: ConvergenceStatus }): JSX.Element {
  const { converged, delta } = status;

  return (
    <div
      data-testid="convergence-status"
      aria-live="polite"
      aria-label={converged ? "Plan converge con los objetivos" : "Plan no converge con los objetivos"}
      className={`rounded-lg px-3 py-2 text-sm ${
        converged
          ? "border border-emerald-800/60 bg-emerald-950/20 text-emerald-400"
          : "border border-amber-800/60 bg-amber-950/20 text-amber-400"
      }`}
    >
      {converged ? (
        <span>Objetivo logrado — el plan converge con tus macros objetivo.</span>
      ) : delta !== undefined ? (
        <ConvergenceWarning delta={delta} />
      ) : (
        <ConvergenceWarning />
      )}
    </div>
  );
}

function ConvergenceWarning({ delta }: { delta?: MacroDelta | undefined }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium">
        Ajuste necesario — el plan no converge completamente con los macros objetivo.
      </span>
      {delta ? (
        <ul
          aria-label="Diferencia entre real y objetivo"
          className="mt-1 list-none text-xs text-amber-300"
        >
          <li>
            Energía: <strong>{formatDelta(delta.kcal)} kcal</strong>
          </li>
          <li>
            Proteínas: <strong>{formatDelta(delta.protein)} g</strong>
          </li>
          <li>
            Grasas: <strong>{formatDelta(delta.fat)} g</strong>
          </li>
          <li>
            Carbohidratos: <strong>{formatDelta(delta.carbs)} g</strong>
          </li>
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Target summary
// ---------------------------------------------------------------------------

function TargetSummary({
  targets,
}: {
  targets: MealPlan["targets"];
}): JSX.Element {
  const { targetKcal, macros } = targets;

  return (
    <section aria-label="Objetivos nutricionales">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-400">
        Objetivo diario
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
        <MacroItem label="Energía" value={formatKcal(targetKcal)} unit="kcal" />
        <MacroItem label="Proteínas" value={formatMacro(macros.proteinG)} unit="g" />
        <MacroItem label="Grasas" value={formatMacro(macros.fatG)} unit="g" />
        <MacroItem label="Carbohidratos" value={formatMacro(macros.carbG)} unit="g" />
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Meal section
// ---------------------------------------------------------------------------

function MealSection({ meal }: { meal: Meal }): JSX.Element {
  return (
    <section aria-label={meal.label}>
      <h3 className="mb-2 text-sm font-semibold text-zinc-300">{meal.label}</h3>
      <ul aria-label={`Alimentos de ${meal.label}`} className="flex flex-col gap-1.5">
        {meal.items.map((item) => (
          <FoodItemRow key={item.foodName} item={item} />
        ))}
      </ul>
    </section>
  );
}

function FoodItemRow({ item }: { item: MealItem }): JSX.Element {
  return (
    <li className="grid grid-cols-[1fr_auto] gap-2 rounded-lg border border-zinc-800 bg-zinc-800/50 px-3 py-2 text-sm">
      {/* Food name — textContent binding, safe against XSS */}
      <span className="text-zinc-100">{item.foodName}</span>
      <div className="flex shrink-0 items-center gap-3 text-xs text-zinc-400">
        <span aria-label={`${formatGrams(item.grams)} gramos`}>{formatGrams(item.grams)} g</span>
        <span aria-label={`${formatKcal(item.kcal)} kilocalorías`}>{formatKcal(item.kcal)} kcal</span>
        <span aria-label={`${formatMacro(item.proteinG)} gramos de proteína`} className="hidden sm:inline">
          P: {formatMacro(item.proteinG)} g
        </span>
        <span aria-label={`${formatMacro(item.carbG)} gramos de carbohidratos`} className="hidden sm:inline">
          C: {formatMacro(item.carbG)} g
        </span>
        <span aria-label={`${formatMacro(item.fatG)} gramos de grasa`} className="hidden sm:inline">
          G: {formatMacro(item.fatG)} g
        </span>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Day totals
// ---------------------------------------------------------------------------

function TotalsSection({ totals }: { totals: MealPlan["totals"] }): JSX.Element {
  return (
    <section
      aria-label="Totales del día"
      className="rounded-lg border border-zinc-700 bg-zinc-800/30 px-3 py-2"
    >
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-400">
        Totales del día
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
        <MacroItem label="Energía" value={formatKcal(totals.kcal)} unit="kcal" />
        <MacroItem label="Proteínas" value={formatMacro(totals.proteinG)} unit="g" />
        <MacroItem label="Grasas" value={formatMacro(totals.fatG)} unit="g" />
        <MacroItem label="Carbohidratos" value={formatMacro(totals.carbG)} unit="g" />
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Macro key-value pair
// ---------------------------------------------------------------------------

function MacroItem({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit: string;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-zinc-500">{label}</dt>
      <dd className="font-medium text-zinc-200">
        {value} <span className="text-xs font-normal text-zinc-400">{unit}</span>
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent rationale
// ---------------------------------------------------------------------------

function RationaleSection({ rationale }: { rationale: string }): JSX.Element {
  return (
    <section aria-label="Comentario del agente nutricional">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-400">
        Comentario
      </h3>
      {/* textContent binding — React renders as text node, never as HTML */}
      <p className="text-sm leading-relaxed text-zinc-400">{rationale}</p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("es-AR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}
